import {
    computed,
    Context,
    createStore,
    ExtendedModel,
    idProp,
    Model,
    model,
    override,
    prop,
} from '@weavix/mobx';
import { SyncService } from '@weavix/sync';
import { BadgeUpdate } from '@weavix/models/src/badges/badge-update';
import { companiesContext } from '../companies-store/companies-store';
import { craftsContext } from '../crafts/crafts-store';
import { myProfileContext, myUserId } from '../my-profile-store/my-profile-store';
import { accountPersonRef } from '../people-store/people-store';
import { environmentContext } from '../environment';
import { Topic } from '@weavix/models/src/topic/topic';
import { LazyObservable } from '@weavix/utils/src/lazy-observable';
import { TeamsIdentity } from '@weavix/models/src/user/User';
import { Voice } from '@weavix/models/src/user/voice';
import { i18nDialect, i18nLocale } from '@weavix/models/src/translate/translate';
import { accountCache, userChannelCache } from 'models-mobx/channels-store/channel';
import { channelsContext } from 'models-mobx/channels-store/channels-store';
import { TranslationService } from 'weavix-shared/services/translation.service';
import { ServiceLocator } from 'weavix-shared/services/service-locator';

export const myUser = () => usersContext.getDefault().get(myUserId());

// unsubscribe from availability updates after this long of no one being interested
const UNSUBSCRIBE_AVAILABILITY_AFTER = 60_000;

const CHANNEL_DEFINING_FIELDS = new Set([
    'firstName',
    'lastName',
    'accountIds',
    'removedAccountIds',
]);

@model('User')
export class User extends Model({
    id: idProp,
    aboutMe: prop<string>(),
    avatarFile: prop<string>(),
    bleIds: prop<number[]>(),
    companyId: prop<string>(),
    email: prop<string>(),
    firstName: prop<string>(),
    lastName: prop<string>(),
    locale: prop<i18nLocale>(),
    dialect: prop<i18nDialect>(),
    /** The user's other spoken language, when they have one; dropped by sync unless declared here. */
    secondaryLocale: prop<i18nLocale>(),
    /** Voice others hear for this user's read-aloud messages; unset means `DEFAULT_VOICE`. Dropped by sync unless declared here. */
    voice: prop<Voice>(),
    middleName: prop<string>(),
    phone: prop<string>(),
    accountIds: prop<string[]>([]),
    deleted: prop<boolean>(),
    teamsOnlyUser: prop<boolean>(),
    teamsId: prop<TeamsIdentity>(),
    acsUserId: prop<string>(),
    removedAccountIds: prop<string[]>([]),
}) {
    private badges: BadgeUpdate[] = [];

    constructor(values?: Partial<User>, unobservable?: boolean) {
        super(values, unobservable);
        this.invalidateChannelUserCache();
    }

    invalidateChannelUserCache(adcAccountIds?: string[]) {
        if (!channelsContext.getDefault()) return;
        const channels = userChannelCache.get(this.id);
        if (channels) {
            channels.forEach(channel => {
                channelsContext.getDefault().getChannelById(channel)?.invalidateUserCache();
            });
        }
        if (adcAccountIds) {
            adcAccountIds.forEach(accountId => accountCache.delete(accountId));
            const adcChannels = channelsContext.getDefault().channels?.filter(x => adcAccountIds.includes(x.accountId));
            adcChannels?.forEach(channel => {
                channel.invalidateUserCache();
            });
        }
    }

    available$ = new LazyObservable<boolean>(UNSUBSCRIBE_AVAILABILITY_AFTER, () => this.startAvailabilityUpdates());

    private startAvailabilityUpdates() {
        const accountIds = this.myAccountIds ?? this.accountIds ?? [];
        const subs = accountIds.map(a => {
            return environmentContext.get().pubSubService.subscribe<any>(null, Topic.AccountPersonFacilitiesUpdated, [a, this.id]).subscribe(({ payload }) => {
                this.updateAccountBadge(a, payload);
            });
        });
        return () => subs.forEach(x => x.unsubscribe());
    }

    @computed
    get company() {
        return companiesContext.get().get(this.companyId);
    }

    get myAccountIds() {
        return myUser().accountIds.filter(v => this.accountIds.includes(v));
    }

    get accounts() {
        return this.accountIds.map(x => companiesContext.get().get(x));
    }

    @computed
    get fullNameTrim() {
        return `${this.firstName?.trim() ?? ''} ${this.lastName?.trim() ?? ''}`.trim();
    }

    @computed
    get searchText() {
        return `${this.fullName}|${this.company?.name ?? ''}|${this.email}|${this.phone}`?.toLowerCase();
    }

    @computed
    get inactiveUser() {
        return this.accountIds.length === 0;
    }

    @computed
    get firstNameLastInitial() {
        const name = `${this.firstName?.trim() ?? ''} ${this.lastName?.trim()?.[0] ?? ''}`.trim();
        return this.appendInactive(name);
    }

    get fullName() {
        return this.appendInactive(this.fullNameTrim);
    }

    get firstNameInactive() {
        return this.appendInactive(this.firstName);
    }

    get lastNameInactive() {
        return this.appendInactive(this.lastName);
    }

    private appendInactive(name: string) {
        return this.inactiveUser ? ServiceLocator.get(TranslationService).getImmediate(('generics.inactive-user'), { name }) : name;
    }

    get available(): boolean {
        return this.badges.some(x => x.facilities?.length);
    }

    get connected(): boolean {
        return this.badges.some(x => Date.now() - new Date(x.heartbeat).getTime() < 5 * 60_000);
    }

    get companyName() {
        return this.company?.name;
    }

    get companyLogo() {
        return this.company?.logo;
    }

    get companyColor() {
        return this.company?.color;
    }

    @computed
    get crafts() {
        return this.accountIds.flatMap(x => accountPersonRef(x, this.id).maybeCurrent?.crafts ?? []);
    }

    @computed
    get accountPerson() {
        const resp = [];
        this.accountIds.map(x => resp.push(accountPersonRef(x, this.id).maybeCurrent));
        resp.push(accountPersonRef(this.companyId, this.id).maybeCurrent);
        return resp.filter(Boolean);
    }

    @computed
    get craftNames() {
        return this.crafts.map(x => craftsContext.get().get(x)?.name).filter(Boolean);
    }

    get badge() {
        return null;
    }

    get onSiteAccounts() {
        return this.badges.filter(x => x.facilities?.length).map(x => x.accountId);
    }

    get onSiteBadges() {
        return this.badges.filter(x => x.facilities?.length).filter(x => this.onSiteAccounts.includes(x.accountId));
    }

    updateAccountBadge(accountId: string, badge: BadgeUpdate) {
        (badge as any).accountId = accountId;
        const found = this.badges.findIndex(x => (x as any).accountId === accountId);
        if (found >= 0) this.badges[found] = badge;
        else this.badges.push(badge);
        this.available$.next(this.available);
    }
}

const base = createStore<User>('users', User);

export const usersContext = base.context as Context<UsersStore>;
export const userRef = base.ref;

@model('UsersStore')
export class UsersStore extends ExtendedModel(base.Store, {
}) {

    getUserByTeamsId(teamsId: string) {
        return usersContext.getDefault().getAllStatic(user => user?.teamsId?.id === teamsId)[0] || null;
    }

    getUserByAcsId(acsUserId: string) {
        return usersContext.getDefault().getAllStatic(user => user?.acsUserId === acsUserId)[0] || null;
    }

    async searchUsers(text: string) {
        try {
            const users = await SyncService.instance.get<any[]>('/sync/users/search', { text });
            return users.map(user => {
                return this.get(user.id, user);
            });
        } catch (e) {
            console.error(e);
        }
    }

    @override
    updateValue(value: any, keys?: any) {
        super.updateValue(value, keys);
        this.updateMyUser(value);
        if (!keys || (keys as any[]).some(key => CHANNEL_DEFINING_FIELDS.has(key))) this.invalidateChannelUserCache(value);
    }

    updateMyUser(value: Partial<User>) {
        if (value.id === myUserId()) {
            // NOTE: we might need to be updating the entire myProfileContext, not just these fields.
            myProfileContext.getDefault().updateUser({ locale: value.locale, dialect: value.dialect });
        }
    }

    invalidateChannelUserCache(value: any) {
        const accountList = [...(value.accountIds ?? []), ...(value.removedAccountIds ?? [])];
        if (!channelsContext.getDefault()) return;
        const channels = userChannelCache.get(value.id);
        if (channels) {
            channels.forEach(channel => channelsContext.getDefault().getChannelById(channel).clearUserCache());
        }
        if (accountList.length > 0) {
            accountList.forEach(accountId => accountCache.delete(accountId));
            const adcChannels = channelsContext.getDefault().channels?.filter(x => accountList.includes(x.accountId));
            adcChannels?.forEach(channel => channel.clearUserCache());
        }
    }
}
